import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    cursorForHandle,
    drawBox,
    HandleName,
    ImageCanvasViewport,
    ImageRect,
    moveBox,
    resizeBox,
} from './image-canvas-viewport';
import { TITLE_FONT } from './image-canvas-labels';
import {
    drawHandles,
    drawSizeLabels,
    drawTitle,
    strokeCanvasRect,
} from './image-canvas-renderer';

export interface CanvasBox {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    color: string;
    label?: string;
    labelBelow?: boolean;
    dashed?: boolean;
}

export type CanvasBoxStyle = Omit<
    CanvasBox,
    'id' | 'x' | 'y' | 'width' | 'height'
>;

/** Anything with an id and an optional bounding box, e.g. an ImgAnnotation. */
export interface CanvasBoxSource {
    id: string;
    bbox?: { x: number; y: number; width: number; height: number } | null;
}

export function toCanvasBox(
    source: CanvasBoxSource,
    style: CanvasBoxStyle
): CanvasBox {
    return {
        id: source.id,
        x: source.bbox?.x ?? 0,
        y: source.bbox?.y ?? 0,
        width: source.bbox?.width ?? 0,
        height: source.bbox?.height ?? 0,
        ...style,
    };
}

export interface CanvasBoxChange {
    id: string;
    bbox: ImageRect;
}

const DRAG_THRESHOLD = 3;
/** Pixel wheel delta per zoom step; a mouse notch is ~100, trackpads send small deltas. */
const WHEEL_PIXELS_PER_STEP = 50;

export type SelectionCycleModifier =
    | 'Alt'
    | 'Control'
    | 'Meta'
    | 'Shift'
    | null;

type Drag =
    | { mode: 'pan'; lastX: number; lastY: number }
    | {
          mode: 'move';
          id: string;
          startCssX: number;
          startCssY: number;
          start: ImageRect;
          moved: boolean;
      }
    | {
          mode: 'resize';
          id: string;
          handle: HandleName;
          startCssX: number;
          startCssY: number;
          start: ImageRect;
      }
    | {
          mode: 'draw';
          startCssX: number;
          startCssY: number;
          startImageX: number;
          startImageY: number;
          boxId?: string;
      };

@Component({
    selector: 'app-image-canvas-viewport',
    templateUrl: './image-canvas-viewport.component.html',
    styleUrls: ['./image-canvas-viewport.component.scss'],
    standalone: false,
})
export class ImageCanvasViewportComponent
    implements OnChanges, AfterViewInit, OnDestroy
{
    @Input() imageUrl: string;
    @Input() imageWidth = 0;
    @Input() imageHeight = 0;
    @Input() boxes: CanvasBox[] = [];
    @Input() selectedId: string | null = null;
    @Input() panEnabled = false;
    @Input() editable = false;
    /** When omitted, drawing follows `editable` for backward compatibility. */
    @Input() drawable: boolean | null = null;
    @Input() selectable = false;
    /**
     * Modifier required to cycle overlapping annotations. `null` (default)
     * cycles with a plain click.
     */
    @Input() selectionCycleModifier: SelectionCycleModifier = null;
    @Input() showSizeLabels = false;
    @Input() maxLevel = 20;
    @Input() level = 0;

    @Output() levelChange = new EventEmitter<number>();
    @Output() annotationSelect = new EventEmitter<string | null>();
    @Output() drawEnd = new EventEmitter<ImageRect>();
    @Output() moveStart = new EventEmitter<void>();
    @Output() resizeStart = new EventEmitter<void>();
    @Output() modifyEnd = new EventEmitter<CanvasBoxChange>();
    @Output() modifyCancel = new EventEmitter<void>();
    @Output() imageLoaded = new EventEmitter<void>();

    @ViewChild('surface') surface: ElementRef<HTMLCanvasElement>;

    readonly viewport = new ImageCanvasViewport();

    private bitmap: HTMLImageElement | null = null;
    private imageGeneration = 0;
    private resizeObserver: ResizeObserver;
    private drag: Drag | null = null;
    private preview: ImageRect | null = null;
    private previewId: string | null = null;
    private tempBox: ImageRect | null = null;
    private viewReady = false;
    private removeSurfaceListeners: () => void;
    /** Stable order for cycling a stack of boxes, independent of z-order changes. */
    private cycleOrder: string[] = [];
    private readonly labelWidths = new Map<string, number>();
    private drawFrame: number | null = null;
    private wheelDelta = 0;

    constructor(
        private host: ElementRef<HTMLElement>,
        private zone: NgZone
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['maxLevel']) {
            this.viewport.setMaxLevel(this.maxLevel);
        }
        if (changes['imageWidth'] || changes['imageHeight']) {
            this.viewport.setImageSize(this.imageWidth, this.imageHeight);
            this.viewport.refit();
        }
        if (changes['level']) {
            this.viewport.setLevel(this.level ?? 0);
        }
        if (changes['imageUrl']) {
            this.cancelDrag();
            this.loadImage();
        }
        if (this.viewReady) {
            this.draw();
        }
    }

    ngAfterViewInit(): void {
        this.viewReady = true;
        this.syncSurface();
        this.viewport.refit();
        this.viewport.setLevel(this.level ?? 0);
        this.zone.runOutsideAngular(() => this.attachSurfaceListeners());
        this.draw();
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        this.removeSurfaceListeners?.();
        this.detachWindowDrag();
        this.imageGeneration++;
        if (this.drawFrame !== null) {
            cancelAnimationFrame(this.drawFrame);
        }
    }

    /**
     * Pointer, wheel and resize events fire at high frequency and only repaint
     * the canvas, so they run outside Angular; outputs re-enter the zone.
     */
    private attachSurfaceListeners(): void {
        const canvas = this.surface.nativeElement;
        const onDown = (event: MouseEvent) => this.onPointerDown(event);
        const onMove = (event: MouseEvent) => this.onPointerMove(event);
        const onWheel = (event: WheelEvent) => this.onWheel(event);
        canvas.addEventListener('mousedown', onDown);
        canvas.addEventListener('mousemove', onMove);
        canvas.addEventListener('wheel', onWheel, { passive: false });
        this.removeSurfaceListeners = () => {
            canvas.removeEventListener('mousedown', onDown);
            canvas.removeEventListener('mousemove', onMove);
            canvas.removeEventListener('wheel', onWheel);
        };
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.onResize());
            this.resizeObserver.observe(this.host.nativeElement);
        }
    }

    private emit<T>(emitter: EventEmitter<T>, value?: T): void {
        if (NgZone.isInAngularZone()) {
            emitter.emit(value);
        } else {
            this.zone.run(() => emitter.emit(value));
        }
    }

    refit(): void {
        if (this.viewReady) {
            this.syncSurface();
        }
        this.viewport.refit();
        this.draw();
    }

    getVisibleImageRect(): ImageRect {
        return this.viewport.getVisibleImageRect();
    }

    onPointerDown(event: MouseEvent): void {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();
        const point = this.cssPoint(event);

        if (this.panEnabled) {
            this.drag = {
                mode: 'pan',
                lastX: event.clientX,
                lastY: event.clientY,
            };
            this.attachWindowDrag();
            this.updateCursor('grabbing');
            return;
        }

        const action = this.viewport.classifyPointer(
            point.x,
            point.y,
            this.hitBoxes(),
            this.selectedId,
            this.editable,
            this.drawable ?? this.editable
        );

        if (action.type === 'resize') {
            const box = this.boxById(action.id);
            if (!box) {
                return;
            }
            this.drag = {
                mode: 'resize',
                id: action.id,
                handle: action.handle,
                startCssX: point.x,
                startCssY: point.y,
                start: this.rectOf(box),
            };
            this.emit(this.resizeStart);
            this.attachWindowDrag();
            this.updateCursor(cursorForHandle(action.handle));
            return;
        }

        if (action.type === 'move') {
            const box = this.boxById(action.id);
            if (!box) {
                return;
            }
            this.drag = {
                mode: 'move',
                id: action.id,
                startCssX: point.x,
                startCssY: point.y,
                start: this.rectOf(box),
                moved: false,
            };
            this.attachWindowDrag();
            this.updateCursor('grabbing');
            return;
        }

        if (action.type === 'select' && this.selectable) {
            this.emitSelectionAt(point.x, point.y, event);
            return;
        }

        if (action.type === 'select-or-draw' || action.type === 'draw') {
            const imagePoint = this.viewport.canvasToImage(point.x, point.y);
            this.drag = {
                mode: 'draw',
                startCssX: point.x,
                startCssY: point.y,
                startImageX: imagePoint.x,
                startImageY: imagePoint.y,
                boxId: action.type === 'select-or-draw' ? action.id : undefined,
            };
            this.attachWindowDrag();
            this.updateCursor('crosshair');
            return;
        }

        if (action.type === 'none') {
            this.emit(this.annotationSelect, null);
        }
    }

    onPointerMove(event: MouseEvent): void {
        if (this.drag) {
            return;
        }
        const point = this.cssPoint(event);
        this.updateCursor(this.hoverCursor(point.x, point.y));
    }

    private onWindowMove(event: MouseEvent): void {
        if (!this.drag) {
            return;
        }
        if (this.drag.mode === 'pan') {
            this.viewport.panBy(
                event.clientX - this.drag.lastX,
                event.clientY - this.drag.lastY
            );
            this.drag.lastX = event.clientX;
            this.drag.lastY = event.clientY;
            this.draw();
            return;
        }

        const point = this.cssPoint(event);
        if (this.drag.mode === 'draw') {
            const distance = Math.hypot(
                point.x - this.drag.startCssX,
                point.y - this.drag.startCssY
            );
            if (this.drag.boxId && distance < DRAG_THRESHOLD) {
                return;
            }
            const current = this.viewport.canvasToImage(point.x, point.y);
            this.tempBox = drawBox(
                { x: this.drag.startImageX, y: this.drag.startImageY },
                current,
                this.viewport.imageWidth,
                this.viewport.imageHeight
            );
            this.draw();
            return;
        }

        const scale = this.viewport.scale || 1;
        const dx = (point.x - this.drag.startCssX) / scale;
        const dy = (point.y - this.drag.startCssY) / scale;
        if (
            this.drag.mode === 'move' &&
            !this.drag.moved &&
            Math.hypot(
                point.x - this.drag.startCssX,
                point.y - this.drag.startCssY
            ) < DRAG_THRESHOLD
        ) {
            return;
        }
        if (this.drag.mode === 'move' && !this.drag.moved) {
            this.drag.moved = true;
            this.emit(this.moveStart);
        }
        const next =
            this.drag.mode === 'move'
                ? moveBox(
                      this.drag.start,
                      dx,
                      dy,
                      this.viewport.imageWidth,
                      this.viewport.imageHeight
                  )
                : resizeBox(
                      this.drag.start,
                      this.drag.handle,
                      dx,
                      dy,
                      this.viewport.imageWidth,
                      this.viewport.imageHeight
                  );
        this.previewId = this.drag.id;
        this.preview = next;
        this.draw();
    }

    private onWindowUp(event: MouseEvent): void {
        const drag = this.drag;
        this.detachWindowDrag();
        this.drag = null;
        if (!drag) {
            return;
        }

        if (drag.mode === 'pan') {
            this.updateCursor('grab');
            return;
        }

        if (drag.mode === 'draw') {
            const point = this.cssPoint(event);
            const distance = Math.hypot(
                point.x - drag.startCssX,
                point.y - drag.startCssY
            );
            const current = this.viewport.canvasToImage(point.x, point.y);
            const drawn = drawBox(
                { x: drag.startImageX, y: drag.startImageY },
                current,
                this.viewport.imageWidth,
                this.viewport.imageHeight
            );
            this.tempBox = null;
            this.draw();
            if (drag.boxId && distance < DRAG_THRESHOLD) {
                this.emitSelectionAt(point.x, point.y, event);
                return;
            }
            if (drawn.width > 0 && drawn.height > 0) {
                this.emit(this.drawEnd, drawn);
            } else {
                this.emit(this.annotationSelect, null);
            }
            return;
        }

        if (drag.mode === 'move' && !drag.moved) {
            const point = this.cssPoint(event);
            this.preview = null;
            this.previewId = null;
            this.draw();
            this.emitSelectionAt(point.x, point.y, event);
            return;
        }

        const bbox = this.preview;
        const id = this.previewId;
        this.preview = null;
        this.previewId = null;
        this.draw();
        if (bbox && id) {
            this.emit(this.modifyEnd, { id, bbox });
        } else {
            this.emit(this.modifyCancel);
        }
    }

    private onWheel(event: WheelEvent): void {
        if (!this.imageWidth || !this.imageHeight || event.deltaY === 0) {
            return;
        }
        event.preventDefault();
        const direction = this.wheelStep(event);
        if (direction === 0) {
            return;
        }
        const point = this.cssPoint(event);
        const before = this.viewport.level;
        this.viewport.zoomAt(point.x, point.y, direction);
        if (this.viewport.level !== before) {
            this.emit(this.levelChange, this.viewport.level);
        }
        this.draw();
    }

    /**
     * Line/page wheels zoom one step per event. Pixel wheels accumulate so a
     * trackpad gesture does not zoom one step per tiny event.
     */
    private wheelStep(event: WheelEvent): 1 | -1 | 0 {
        if (event.deltaMode !== WheelEvent.DOM_DELTA_PIXEL) {
            this.wheelDelta = 0;
            return event.deltaY < 0 ? 1 : -1;
        }
        if (Math.sign(event.deltaY) !== Math.sign(this.wheelDelta)) {
            this.wheelDelta = 0;
        }
        this.wheelDelta += event.deltaY;
        if (Math.abs(this.wheelDelta) < WHEEL_PIXELS_PER_STEP) {
            return 0;
        }
        const direction = this.wheelDelta < 0 ? 1 : -1;
        this.wheelDelta = 0;
        return direction;
    }

    private onResize(): void {
        this.syncSurface();
        this.viewport.refit();
        this.draw();
    }

    private loadImage(): void {
        const generation = ++this.imageGeneration;
        this.bitmap = null;
        if (!this.imageUrl) {
            this.draw();
            return;
        }
        const image = new Image();
        image.onload = () => {
            if (generation !== this.imageGeneration) {
                return;
            }
            this.bitmap = image;
            this.emit(this.imageLoaded);
            this.draw();
        };
        image.onerror = () => {
            if (generation !== this.imageGeneration) {
                return;
            }
            this.bitmap = null;
            this.draw();
        };
        image.src = this.imageUrl;
    }

    private syncSurface(): void {
        const host = this.host.nativeElement;
        const dpr = window.devicePixelRatio || 1;
        this.viewport.setSurface(host.clientWidth, host.clientHeight, dpr);
        const canvas = this.surface?.nativeElement;
        if (!canvas) {
            return;
        }
        if (
            canvas.width !== this.viewport.backingWidth ||
            canvas.height !== this.viewport.backingHeight
        ) {
            canvas.width = this.viewport.backingWidth;
            canvas.height = this.viewport.backingHeight;
        }
    }

    /** Coalesces repaints to one per animation frame, outside Angular. */
    private draw(): void {
        if (this.drawFrame !== null) {
            return;
        }
        this.drawFrame = this.zone.runOutsideAngular(() =>
            requestAnimationFrame(() => {
                this.drawFrame = null;
                this.render();
            })
        );
    }

    private render(): void {
        const canvas = this.surface?.nativeElement;
        const context = canvas?.getContext('2d');
        if (!canvas || !context) {
            return;
        }
        const dpr = this.viewport.dpr || 1;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
        context.clearRect(0, 0, this.viewport.areaW, this.viewport.areaH);
        context.imageSmoothingEnabled = this.viewport.scale <= 1;

        const geometry = this.viewport.viewGeometry();
        if (this.bitmap && this.imageWidth > 0 && this.imageHeight > 0) {
            context.save();
            context.translate(geometry.originX, geometry.originY);
            context.scale(geometry.scale, geometry.scale);
            context.drawImage(
                this.bitmap,
                0,
                0,
                this.imageWidth,
                this.imageHeight
            );
            context.restore();
        }

        for (const box of this.boxes ?? []) {
            this.drawBox(context, this.displayedRect(box), box);
        }
        if (this.tempBox) {
            this.strokeRect(context, this.tempBox, '#ffb74d', true);
        }
    }

    private drawBox(
        context: CanvasRenderingContext2D,
        rect: ImageRect,
        box: CanvasBox
    ): void {
        const canvasRect = this.viewport.imageRectToCanvas(rect);
        const selected = box.id === this.selectedId;
        const color = selected ? '#ffffff' : box.color;
        this.strokeRect(context, rect, color, !!box.dashed);

        if (box.label) {
            this.drawTitle(
                context,
                canvasRect,
                box.label,
                color,
                !!box.labelBelow
            );
        }
        if (this.showSizeLabels) {
            this.drawSizeLabels(context, canvasRect, rect, color);
        }
        if (selected && this.editable) {
            drawHandles(context, canvasRect, color);
        }
    }

    private strokeRect(
        context: CanvasRenderingContext2D,
        rect: ImageRect,
        color: string,
        dashed: boolean
    ): void {
        strokeCanvasRect(
            context,
            this.viewport.imageRectToCanvas(rect),
            color,
            dashed
        );
    }

    private drawTitle(
        context: CanvasRenderingContext2D,
        canvasRect: ImageRect,
        label: string,
        color: string,
        below: boolean
    ): void {
        drawTitle(
            context,
            canvasRect,
            label,
            this.measureLabel(label),
            color,
            below,
            this.viewport.imageBounds()
        );
    }

    private drawSizeLabels(
        context: CanvasRenderingContext2D,
        canvasRect: ImageRect,
        rect: ImageRect,
        color: string
    ): void {
        drawSizeLabels(
            context,
            canvasRect,
            rect,
            color,
            this.viewport.imageBounds()
        );
    }

    /** Cached so cursor hit-testing on mousemove does not call measureText per label. */
    private measureLabel(label: string): number {
        let width = this.labelWidths.get(label);
        if (width === undefined) {
            const context = this.surface?.nativeElement.getContext('2d');
            if (!context) {
                return label.length * 7;
            }
            context.save();
            context.font = TITLE_FONT;
            width = context.measureText(label).width;
            context.restore();
            this.labelWidths.set(label, width);
        }
        return width;
    }

    private hoverCursor(cssX: number, cssY: number): string {
        if (this.panEnabled) {
            return 'grab';
        }
        const action = this.viewport.classifyPointer(
            cssX,
            cssY,
            this.hitBoxes(),
            this.selectedId,
            this.editable,
            this.drawable ?? this.editable
        );
        if (action.type === 'resize') {
            return cursorForHandle(action.handle);
        }
        if (action.type === 'move') {
            return 'move';
        }
        if (action.type === 'select' && this.selectable) {
            return 'pointer';
        }
        if (action.type === 'draw' || action.type === 'select-or-draw') {
            return 'crosshair';
        }
        return 'default';
    }

    private displayedRect(box: CanvasBox): ImageRect {
        if (this.preview && this.previewId === box.id) {
            return this.preview;
        }
        return this.rectOf(box);
    }

    private rectOf(box: CanvasBox): ImageRect {
        return { x: box.x, y: box.y, width: box.width, height: box.height };
    }

    private hitBoxes() {
        return (this.boxes ?? []).map((box) => {
            const rect = this.displayedRect(box);
            return {
                id: box.id,
                ...rect,
                label: box.label,
                labelBelow: box.labelBelow,
                labelWidth: box.label ? this.measureLabel(box.label) : 0,
            };
        });
    }

    private boxById(id: string): CanvasBox | undefined {
        return (this.boxes ?? []).find((box) => box.id === id);
    }

    private emitSelectionAt(
        cssX: number,
        cssY: number,
        event: MouseEvent
    ): void {
        const candidates = this.viewport.hitBoxIds(cssX, cssY, this.hitBoxes());
        if (!candidates.length) {
            this.emit(this.annotationSelect, null);
            return;
        }

        const cycle =
            this.selectionCycleModifier === null ||
            this.isCycleModifierPressed(event);
        if (!this.isSameStack(candidates, this.cycleOrder)) {
            this.cycleOrder = candidates;
        }
        const currentIndex = this.cycleOrder.indexOf(this.selectedId ?? '');
        const nextId =
            cycle && currentIndex >= 0
                ? this.cycleOrder[(currentIndex + 1) % this.cycleOrder.length]
                : candidates[0];
        if (nextId !== this.selectedId) {
            this.emit(this.annotationSelect, nextId);
        }
    }

    /** Callers may reorder boxes on selection, so compare stacks as sets. */
    private isSameStack(candidates: string[], previous: string[]): boolean {
        return (
            candidates.length === previous.length &&
            candidates.every((id) => previous.includes(id))
        );
    }

    private isCycleModifierPressed(event: MouseEvent): boolean {
        switch (this.selectionCycleModifier) {
            case 'Alt':
                return event.altKey;
            case 'Control':
                return event.ctrlKey;
            case 'Meta':
                return event.metaKey;
            case 'Shift':
                return event.shiftKey;
            default:
                return false;
        }
    }

    private cssPoint(event: MouseEvent): { x: number; y: number } {
        const rect = this.surface.nativeElement.getBoundingClientRect();
        return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    }

    private updateCursor(cursor: string): void {
        if (this.surface?.nativeElement) {
            this.surface.nativeElement.style.cursor = cursor;
        }
    }

    private attachWindowDrag(): void {
        window.addEventListener('mousemove', this.boundMove);
        window.addEventListener('mouseup', this.boundUp);
    }

    private detachWindowDrag(): void {
        window.removeEventListener('mousemove', this.boundMove);
        window.removeEventListener('mouseup', this.boundUp);
    }

    private cancelDrag(): void {
        const drag = this.drag;
        this.drag = null;
        this.preview = null;
        this.previewId = null;
        this.tempBox = null;
        this.detachWindowDrag();
        if (!drag) {
            return;
        }
        if (drag.mode === 'pan') {
            this.updateCursor('grab');
            return;
        }
        // moveStart/resizeStart were only emitted for resizes and real moves
        if (drag.mode === 'resize' || (drag.mode === 'move' && drag.moved)) {
            this.emit(this.modifyCancel);
        }
    }

    private readonly boundMove = (event: MouseEvent) =>
        this.onWindowMove(event);
    private readonly boundUp = (event: MouseEvent) => this.onWindowUp(event);
}
